import { type FC, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { RiAddLine } from 'react-icons/ri';
import { RiCloseFill } from 'react-icons/ri';
import { _notNil } from '@/littledash';
import {
  type GroupDimension,
  type MetaField,
  type StaticGroupDimension,
  STATIC_DIMENSION_LABELS,
} from './SampleNames.model';
import { dimensionKey, getDimensionLabel } from './SampleNames.utils';
import styles from './GroupingControls.module.scss';

/**
 * Chip-based control bar for selecting up to three grouping dimensions on the sample names tree.
 * Renders active groupings as removable chips and a keyboard-accessible dropdown for adding new ones.
 */
interface GroupingControlsProps {
  groupings: Array<GroupDimension>;
  onRemove: (dimension: GroupDimension) => void;
  onAdd: (dimension: GroupDimension) => void;
  metaFields: Array<MetaField>;
  actions?: ReactNode;
}

const dimensionsEqual = (a: GroupDimension, b: GroupDimension): boolean => dimensionKey(a) === dimensionKey(b);

const GroupingControls: FC<GroupingControlsProps> = ({ groupings, onRemove, onAdd, metaFields, actions }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dropdownOpen]);

  const staticAvailable = (Object.keys(STATIC_DIMENSION_LABELS) as Array<StaticGroupDimension>).filter(
    (d) => !groupings.some((g) => dimensionsEqual(g, d))
  );
  const metaAvailable = metaFields.reduce<Array<GroupDimension>>((acc, f) => {
    const dim: GroupDimension = { type: 'sampleMeta', apiId: f.apiId };
    if (!groupings.some((g) => dimensionsEqual(g, dim))) {
      acc.push(dim);
    }
    return acc;
  }, []);
  const available: Array<GroupDimension> = [...staticAvailable, ...metaAvailable];

  const handleAddKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setDropdownOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const handleItemKeyDown = (e: KeyboardEvent<HTMLLIElement>, dimension: GroupDimension) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAdd(dimension);
      setDropdownOpen(false);
    }
  };

  return (
    <div className={styles.container} data-test-component="GroupingControls">
      <span className={styles.label}>Group by</span>
      <div className={styles.chips} data-test-element="chips">
        {groupings.map((dimension) => {
          const key = dimensionKey(dimension);
          return (
            <div key={key} className={styles.chip} data-test-element="chip" data-test-key={key}>
              <span>{getDimensionLabel(dimension, metaFields)}</span>
              <button
                type="button"
                className={styles.removeButton}
                aria-label={`Remove ${getDimensionLabel(dimension, metaFields)} grouping`}
                onClick={() => onRemove(dimension)}
              >
                <RiCloseFill size={14} />
              </button>
            </div>
          );
        })}
      </div>
      {_notNil(available[0]) && groupings.length < 3 && (
        <div
          ref={containerRef}
          className={`${styles.addContainer}${dropdownOpen ? ` ${styles.open}` : ''}`}
          data-test-element="add-container"
        >
          <button
            type="button"
            className={styles.addButton}
            aria-label="Add grouping"
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            data-test-element="add-button"
            onClick={() => setDropdownOpen((prev) => !prev)}
            onKeyDown={handleAddKeyDown}
          >
            <RiAddLine size={14} />
            Add grouping
          </button>
          <ul role="listbox" className={styles.dropdown} data-test-element="dropdown">
            {available.map((dimension) => {
              const key = dimensionKey(dimension);
              return (
                <li
                  key={key}
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  className={styles.dropdownItem}
                  data-test-element="dropdown-item"
                  data-test-key={key}
                  onClick={() => {
                    onAdd(dimension);
                    setDropdownOpen(false);
                  }}
                  onKeyDown={(e) => handleItemKeyDown(e, dimension)}
                >
                  {getDimensionLabel(dimension, metaFields)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {_notNil(actions) && <div className={styles.actions}>{actions}</div>}
    </div>
  );
};

export default GroupingControls;
